import type { HomeDashboardDTO } from "@/api/adapters/home-dashboard";
import type { SectionId } from "../constants";
import { SectionId as S } from "../constants";

import { DelayDetail } from "./DelayDetail";
import { RDangerDetail } from "./RDangerDetail";
import { ScheduleDetail } from "./ScheduleDetail";
import { SeverityDetail } from "./SeverityDetail";

interface Props {
	section: SectionId;
	data: HomeDashboardDTO;
	t: (key: string, opts?: Record<string, unknown>) => string;
	navigate: (path: string) => void;
}

export function DetailPanel({ section, data, t, navigate }: Props) {
	return (
		<>
			<DetailHeader section={section} data={data} t={t} />
			<div className="card__body">
				{section === S.R && (
					<RDangerDetail data={data.dangerR} t={t} navigate={navigate} />
				)}
				{section === S.SEVERITY && (
					<SeverityDetail data={data.todayItems} t={t} navigate={navigate} />
				)}
				{section === S.DELAY && (
					<DelayDetail
						data={data.delayed}
						weekStart={data.weekStart}
						t={t}
						navigate={navigate}
					/>
				)}
				{section === S.SCHEDULE && (
					<ScheduleDetail
						today={data.todaySchedule}
						upcoming={data.upcomingSchedule}
						todayStr={data.todayStr}
						t={t}
						navigate={navigate}
					/>
				)}
			</div>
		</>
	);
}

// ── Detail Header ──

function DetailHeader({
	section,
	data,
	t,
}: {
	section: SectionId;
	data: HomeDashboardDTO;
	t: (key: string, opts?: Record<string, unknown>) => string;
}) {
	switch (section) {
		case S.R:
			return (
				<div className="card__header today-detail__header">
					<h3 className="card__title">
						{t("medical:home.sections.atRisk.title")}
					</h3>
					<span className="today-detail__sub">
						<span className="today-detail__divider">|</span>
						<span className="today-detail__caption">
							{t("medical:home.sections.atRisk.caption", {
								threshold: data.R_DANGER_THRESHOLD,
							})}
							{" · "}
							{t("medical:home.sections.atRisk.hrIncluded")}
							{" · "}
							{t("medical:home.sections.atRisk.unknownIncluded")}
						</span>
						<span className="today-detail__stat">
							<span className="today-detail__stat-value">
								<span className="today-detail__stat-num today-detail__stat-num--red">
									{data.dangerR.length}
								</span>
								<span className="today-detail__stat-unit">
									{t("medical:home.sections.atRisk.unit")}
								</span>
							</span>
						</span>
					</span>
				</div>
			);

		case S.SEVERITY:
			return (
				<div className="card__header today-detail__header">
					<h3 className="card__title">
						{t("medical:home.sections.todayPro.title")}
					</h3>
					<span className="today-detail__sub">
						<span className="today-detail__divider">|</span>
						<span className="today-detail__stat">
							<span className="today-detail__stat-label">
								{t("medical:home.sections.todayPro.items")}
							</span>
							<span className="today-detail__stat-value">
								<span className="today-detail__stat-num">
									{data.todayItems.length}
								</span>
								<span className="today-detail__stat-unit">
									{t("medical:home.sections.todayPro.itemsUnit")}
								</span>
							</span>
						</span>
						<span className="today-detail__divider">|</span>
						<span className="today-detail__stat">
							<span className="today-detail__stat-label">
								{t("medical:home.sections.todayPro.g3Patients")}
							</span>
							<span className="today-detail__stat-value">
								<span className="today-detail__stat-num today-detail__stat-num--red">
									{data.severePatientCount}
								</span>
								<span className="today-detail__stat-unit">
									{t("medical:home.sections.todayPro.unit")}
								</span>
							</span>
						</span>
					</span>
				</div>
			);

		case S.DELAY:
			return (
				<div className="card__header today-detail__header">
					<h3 className="card__title">
						{t("medical:home.sections.responseDelay.title")}
					</h3>
					<span className="today-detail__sub">
						<span className="today-detail__divider">|</span>
						<span className="today-detail__caption">
							{t("medical:home.sections.responseDelay.weekRange", {
								weekStart: data.weekStart,
							})}
						</span>
						<span className="today-detail__divider">|</span>
						<span className="today-detail__stat">
							<span className="today-detail__stat-label">
								{t("medical:home.sections.responseDelay.noResponse")}
							</span>
							<span className="today-detail__stat-value">
								<span className="today-detail__stat-num today-detail__stat-num--amber">
									{data.delayed.length}
								</span>
								<span className="today-detail__stat-unit">
									{t("medical:home.sections.responseDelay.unit")}
								</span>
							</span>
						</span>
					</span>
				</div>
			);

		case S.SCHEDULE:
			return (
				<div className="card__header today-detail__header">
					<h3 className="card__title">
						{t("medical:home.sections.schedule.title")}
					</h3>
					<span className="today-detail__sub">
						<span className="today-detail__divider">|</span>
						<span className="today-detail__stat">
							<span className="today-detail__stat-label">
								{t("medical:home.sections.schedule.today")}
							</span>
							<span className="today-detail__stat-value">
								<span className="today-detail__stat-num">
									{data.todaySchedule.length}
								</span>
								<span className="today-detail__stat-unit">
									{t("medical:home.sections.schedule.unit")}
								</span>
							</span>
						</span>
						<span className="today-detail__stat">
							<span className="today-detail__stat-label">
								{t("medical:home.sections.schedule.upcoming")}
							</span>
							<span className="today-detail__stat-value">
								<span className="today-detail__stat-num">
									{data.upcomingSchedule.length}
								</span>
								<span className="today-detail__stat-unit">
									{t("medical:home.sections.schedule.unit")}
								</span>
							</span>
						</span>
					</span>
				</div>
			);
	}
}
